/**
 * Shared Zod schemas used across the application.
 *
 * IMPORTANT: This file is imported by client-side code.
 * Do NOT import from @prisma/client or any server-only modules here.
 *
 * For TypeScript enums, import directly from src/types/enums in your code.
 * These Zod schemas use string literals to be compatible with both Prisma and GraphQL enums.
 */
import { z } from 'zod';

// ============================================================================
// Order Zod schemas
// ============================================================================
export const OrderStatusEnum = z.enum([
  'Draft',
  'Created',
  'Prepayment',
  'Placed',
  'Confirmed',
  'Refused',
  'Executed',
  'Cancelled',
]);
export const OrderLoadingStatusEnum = z.enum(['NotStarted', 'Loaded', 'Unloaded']);
export const OrderLineStopTypeEnum = z.enum(['Load', 'Unload']);
export const OrderDocumentTypeEnum = z.enum([
  'CMR',
  'SaleInvoice',
  'TransportOrder',
  'OrderConfirmation',
  'SustainabilityReport',
  'CustomerAttachment',
  'DeliveryNote',
  'WeighSlip',
  'PalletNote',
  'AnnexII',
  'T1Document',
  'AnnexVII',
  'Planning',
  'Other',
]);
export const OrderDocumentValidationStatusEnum = z.enum(['NoDocuments', 'Pending', 'Approved', 'Rejected']);
export const IncomingDocumentRejectionReasonEnum = z.enum([
  'LowQuality',
  'MissingSignature',
  'NoOrderReference',
  'NotNeutral',
  'WrongCustomer',
  'WrongDeliveryAddress',
  'WrongDocumentType',
  'WrongOrgOnDocument',
]);

export const SaleStatusEnum = z.enum(['Created', 'Incomplete', 'Invoiced', 'Credited']);

export const CarrierUserRoleEnum = z.enum(['CarrierDispatcher', 'CarrierDriver']);

// ============================================================================
// Supplier / Carrier Zod schemas
// ============================================================================
export const SupplierDocumentTypeEnum = z.enum(['CivilLiabilityInsurance', 'CmrInsurance', 'TransportInsurance']);
export const SupplierTruckDocumentTypeEnum = z.enum(['Examination', 'Insurance']);
export const PaymentTermEnum = z.enum(['Days0', 'Days7', 'Days14', 'Days30', 'Days45', 'Days60', 'Days90']);

// ============================================================================
// Trailer Zod schemas
// ============================================================================
export const TrailerTypeEnum = z.enum([
  'Tautliner',
  'BoxTrailer',
  'FlatBed',
  'Mega',
  'RoadTrain',
  'Refrigerated',
  'Joloda',
  'TautlinerCoil',
]);

// ============================================================================
// Loading Method Zod schemas
// ============================================================================
export const LoadingMethodEnum = z.enum(['Back', 'Side', 'Top']);

// ============================================================================
// Business Unit / Region
// ============================================================================
export const BusinessUnitEnum = z.enum(['Benelux', 'France', 'Germany', 'Forwarding']);

// Prisma stores businessUnit as Int (0, 1, 2, 3)
export const BusinessUnitInt = {
  Benelux: 0,
  France: 1,
  Germany: 2,
  Forwarding: 3,
} as const;

export type Region = 'all' | 'benelux' | 'france' | 'germany' | 'forwarding';
export const RegionEnum = z.enum(['all', 'benelux', 'france', 'germany', 'forwarding']);

export const DateTypeEnum = z.enum(['load', 'unload']);

export function getRegionFromBusinessUnit(businessUnit: number): Region {
  switch (businessUnit) {
    case BusinessUnitInt.Benelux:
      return 'benelux';
    case BusinessUnitInt.France:
      return 'france';
    case BusinessUnitInt.Germany:
      return 'germany';
    case BusinessUnitInt.Forwarding:
      return 'forwarding';
    default:
      return 'benelux';
  }
}

/** Convert Region to Prisma Int value */
export function businessUnitToNumber(region: Region): number | null {
  switch (region) {
    case 'benelux':
      return BusinessUnitInt.Benelux;
    case 'france':
      return BusinessUnitInt.France;
    case 'germany':
      return BusinessUnitInt.Germany;
    case 'forwarding':
      return BusinessUnitInt.Forwarding;
    default:
      return null;
  }
}

// ============================================================================
// Trailer type compatibility
// ============================================================================
export function canTrailerCarryLoad(
  truckTrailerType: z.infer<typeof TrailerTypeEnum> | null,
  loadTrailerTypes: z.infer<typeof TrailerTypeEnum>[],
): boolean {
  if (!truckTrailerType) return true;
  if (loadTrailerTypes.length === 0) return true;

  if (loadTrailerTypes.includes(truckTrailerType)) return true;

  // MEGA trailers can carry Tautliner loads
  if (truckTrailerType === 'Mega' && loadTrailerTypes.includes('Tautliner')) {
    return true;
  }

  return false;
}
